import { css } from "@emotion/react";
import styled from "@emotion/styled";

import type { ModalDialogProps } from "@skilitics/designer-ui-components";
import { ModalDialog } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { useFeatureSwitch, useKeyboardShortcutsVisible } from "@skilitics/nautilus-core-designer";

const Subheading = styled.h4`
  margin: 0 0 15px;
  padding: 0;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 15px;
  width: 400px;
`;

const ListItem = styled.li`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  list-style: none;
  padding: 5px 0;
  margin: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.surfaceVariant};
  margin: 20px 0;
`;

const KeySeparator = styled.span`
  padding: 0 4px;
  display: inline-block;
  vertical-align: middle;
`;
const Key = styled.span`
  display: inline-block;
  vertical-align: middle;
  background: ${({ theme }) => theme.surfaceHighlight};
  border-radius: 4px;
  padding: 4px 8px;
  font-weight: bold;
`;

type KeyboardShortcutProps = {
  keyboardKeys: string[];
  name: string;
};
const KeyboardShortcut = ({ keyboardKeys: keys, name }: KeyboardShortcutProps) => (
  <ListItem>
    <span
      css={css`
        flex: 1 1 100%;
        text-align: right;
        padding-right: 10px;
      `}
    >
      {name}
    </span>
    <span
      css={css`
        flex: 1 0 220px;
      `}
    >
      {keys.map((keyboardKey, index) =>
        ["and", "then", "hold", ","].includes(keyboardKey) ? (
          <KeySeparator key={index}>{keyboardKey}</KeySeparator>
        ) : (
          <Key key={index}>{keyboardKey}</Key>
        ),
      )}
    </span>
  </ListItem>
);

type KeyboardShortcutsDialogProps = ModalDialogProps;

export const KeyboardShortcutsDialog = (props: KeyboardShortcutsDialogProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const [isKeyboardShortcutsVisible, setKeyboardShortcutsVisible] = useKeyboardShortcutsVisible();

  const multipleSelectionAvailable = useFeatureSwitch("module-map-multiple-selection");

  return (
    <ModalDialog
      header={formatMessage("dialogTitle")}
      buttons="showClose"
      max-width="lg"
      open={isKeyboardShortcutsVisible}
      onClose={() => setKeyboardShortcutsVisible(false)}
      {...props}
    >
      <Subheading>{formatMessage("subheaderModuleMap")}</Subheading>
      <List>
        <KeyboardShortcut name={formatMessage("zoomIn")} keyboardKeys={["CTRL", "and", "+"]} />
        <KeyboardShortcut name={formatMessage("zoomOut")} keyboardKeys={["CTRL", "and", "-"]} />
        <KeyboardShortcut name={formatMessage("zoomReset")} keyboardKeys={["CTRL", "and", "0"]} />
        <KeyboardShortcut name={formatMessage("pan")} keyboardKeys={["hold", "Middle-Mouse"]} />
        <KeyboardShortcut name={formatMessage("panHorizontal")} keyboardKeys={["SHIFT", "and", "Scroll"]} />
        <KeyboardShortcut name={formatMessage("panVertical")} keyboardKeys={["Scroll"]} />
        <KeyboardShortcut name={formatMessage("mousePointerReset")} keyboardKeys={["ESC"]} />
        <KeyboardShortcut name={formatMessage("mousePointerHand")} keyboardKeys={["H"]} />
        <KeyboardShortcut name={formatMessage("mousePointerCursor")} keyboardKeys={["S"]} />
        <KeyboardShortcut name={formatMessage("mousePointerTemporaryHand")} keyboardKeys={["hold", "SPACE"]} />
        <KeyboardShortcut name={formatMessage("mousePointerCreateScene")} keyboardKeys={["C"]} />

        {multipleSelectionAvailable && (
          <>
            <KeyboardShortcut name={formatMessage("selectAll")} keyboardKeys={["A"]} />
            <KeyboardShortcut name={formatMessage("selectionMode")} keyboardKeys={["M"]} />
            <KeyboardShortcut name={formatMessage("multipleSelectionMode")} keyboardKeys={["hold", "CTRL"]} />
          </>
        )}
      </List>

      <Divider />
      <Subheading>{formatMessage("subheaderScenes")}</Subheading>
      <List>
        <KeyboardShortcut name={formatMessage("changeSceneCardView")} keyboardKeys={["Z"]} />
        <KeyboardShortcut name={formatMessage("sceneNumberDoubleClick")} keyboardKeys={["Double-Click"]} />
        <KeyboardShortcut name={formatMessage("sceneEditInputs")} keyboardKeys={["Double-Click"]} />
        <KeyboardShortcut name={formatMessage("sceneCancelEditing")} keyboardKeys={["ESC"]} />
      </List>

      <Divider />
      <Subheading>{formatMessage("subheaderConnectors")}</Subheading>
      <List>
        <KeyboardShortcut name={formatMessage("sceneFromConnector")} keyboardKeys={["SHIFT", "and", "CLICK"]} />
        <KeyboardShortcut
          name={formatMessage("removeConnector")}
          keyboardKeys={["CTRL", ",", "SHIFT", "and", "CLICK"]}
        />
      </List>

      <Divider />
      <Subheading>{formatMessage("subHeaderComposable")}</Subheading>
      <List>
        <KeyboardShortcut name={formatMessage("hidePanels")} keyboardKeys={["CTRL", "and", "\\"]} />
      </List>
    </ModalDialog>
  );
};

const formatStrings = {
  dialogTitle: {
    en: "Keyboard Shortcuts",
  },
  subheaderModuleMap: {
    en: "Module Map",
  },
  zoomIn: {
    en: "Zoom In",
  },
  zoomOut: {
    en: "Zoom Out",
  },
  zoomReset: {
    en: "Zoom Reset",
  },
  pan: {
    en: "Pan",
  },
  panHorizontal: {
    en: "Pan Horizontal",
  },
  panVertical: {
    en: "Pan Vertical",
  },
  mousePointerReset: {
    en: "Reset Cursor",
  },
  mousePointerHand: {
    en: "Hand Cursor",
  },
  mousePointerCursor: {
    en: "Pointer Cursor",
  },
  mousePointerTemporaryHand: {
    en: "Temporary Hand Cursor",
  },
  mousePointerCreateScene: {
    en: "Create Scene Cursor",
  },

  selectAll: {
    en: "Select All",
  },
  selectionMode: {
    en: "Selection Mode",
  },
  multipleSelectionMode: {
    en: "Multiple Selection",
  },

  subheaderScenes: {
    en: "Module Map - Scenes",
  },
  changeSceneCardView: {
    en: "Change Scene Card View",
  },
  sceneEditInputs: {
    en: "Edit Scene Information (on Text Fields)",
  },
  sceneCancelEditing: {
    en: "Cancel Editing",
  },
  sceneNumberDoubleClick: {
    en: "Open Scene (on Scene Number)",
  },

  subheaderConnectors: {
    en: "Module Map - While Dragging Connectors",
  },
  sceneFromConnector: {
    en: "Create Scene",
  },
  removeConnector: {
    en: "Remove Connector",
  },

  subHeaderComposable: {
    en: "Composable Screens",
  },
  hidePanels: {
    en: "Hide Panels",
  },
} satisfies FormatMessages;
